import type { ItemList, ListItem, Thing, Product } from 'schema-dts';
import { fetchPlaceholders } from 'scripts/aem.js';
import './article-cards.css';
import { jsonLdScript } from 'utils/linkedDataSchema/linkedDataSchema.ts';
import { BasicEvidentOrganization } from 'utils/linkedDataSchema/types/organization.ts';

function addLinkToTitle(title: HTMLElement, link: string) {
	const cardTitleLink = document.createElement('a');
	cardTitleLink.href = link;
	cardTitleLink.innerText = title.innerText;
	title.innerHTML = '';
	title.append(cardTitleLink);
}

function addItemListSchema(listItems: ListItem[], name: string) {
	if (listItems.length === 0) return;

	const itemList: ItemList = {
		'@type': 'ItemList',
		name,
		url: document.location.origin + document.location.pathname,
		itemListElement: listItems,
	} satisfies ItemList;

	jsonLdScript({
		'@context': 'https://schema.org',
		...itemList,
	});
}

export default async function decorate(block: HTMLElement): Promise<void> {
	const isBlogPage = document.body.classList.contains('blog');
	const visibleCount = isBlogPage
		? 2
		: block.classList.contains('four')
			? 4
			: block.classList.contains('two')
				? 2
				: 3;
	const showAll = block.classList.contains('display-all');

	if (block.children.length === 0) return;

	const [titleElement, ...cardElements] = [...block.children] as HTMLElement[];

	titleElement.classList.add('article-cards__title');
	const articleCards = document.createElement('div');
	articleCards.classList.add('article-cards__cards');

	const listItems: ListItem[] = [];

	cardElements.forEach((cardElementWrapper, index) => {
		const cardElement = cardElementWrapper.firstElementChild;
		if (!cardElement) return;

		// First button encountered is te primary CTA
		const primaryCtaContainer = cardElement.querySelector<HTMLDivElement>(
			'.button-container:has(a)',
		);
		if (primaryCtaContainer) {
			primaryCtaContainer.className = 'article-cards__read-now';
			// Place CTA at bottom of card
			cardElement.append(primaryCtaContainer);
		}

		const primaryCta =
			primaryCtaContainer?.querySelector<HTMLAnchorElement>('a');
		if (primaryCta) primaryCta.classList.add('small');

		// First heading ecountered, if doesn't have link, recieves primary CTA link
		const cardTitle = cardElement.querySelector<HTMLElement>('h2, h3, h4, h5');
		if (cardTitle && primaryCta && !cardTitle.querySelector('a')) {
			addLinkToTitle(cardTitle, primaryCta.href);
		}

		const schemaName = cardTitle?.innerText?.trim();
		const schemaDescription = cardElement
			.querySelector<HTMLParagraphElement>(
				'p:not(:has(picture)):not(.article-cards__read-now)',
			)
			?.innerText?.trim();
		const schemaImage = cardElement.querySelector('img')?.src?.split('?')[0];

		let cardCta = primaryCta?.href;
		if (!cardCta) {
			// If no primary CTA, use first heading link
			cardCta = cardElement.querySelector<HTMLAnchorElement>(
				'*:is(h1, h2, h3, h4, h5) a',
			)?.href;
		}

		let validUrl = false;
		if (cardCta) {
			try {
				new URL(cardCta, window.location.origin);
				validUrl = true;
			} catch (error) {
				validUrl = false;
			}
		}

		if (validUrl && cardCta) {
			const useProductSchema = cardCta.includes('/products/');

			listItems.push({
				'@type': 'ListItem',
				position: index + 1,
				item: useProductSchema
					? ({
							'@type': 'Product',
							name: schemaName,
							description: schemaDescription,
							url: cardCta,
							sku: cardCta?.split('/').filter(Boolean).pop(),
							image: schemaImage,
							brand: BasicEvidentOrganization,
						} satisfies Product)
					: ({
							'@type': 'Thing',
							name: schemaName,
							description: schemaDescription,
							url: cardCta,
							image: schemaImage,
						} satisfies Thing),
			});
		}

		// Wrap pictures in links, first image gets primary CTA if it doesn't have a link
		[...cardElement.querySelectorAll('p:has(picture)')].forEach(
			(paragraphWithPicture, index) => {
				const picture = paragraphWithPicture.querySelector('picture')!;
				let pictureLink = paragraphWithPicture.querySelector('a');
				const pictureContainer = document.createElement('div');

				if (!pictureLink && primaryCta && index === 0) {
					pictureLink = document.createElement('a');
					pictureLink.href = primaryCta.href;
				}

				if (pictureLink) {
					pictureLink.innerHTML = '';
					pictureLink.append(picture);
					if (index === 0) {
						pictureLink.ariaLabel = cardTitle?.innerText || '';
						pictureLink.title = cardTitle?.innerText || pictureLink.title;

						// If we have a title without a link, use first image link on title
						if (cardTitle && !primaryCta && !cardTitle.querySelector('a'))
							addLinkToTitle(cardTitle, pictureLink.href);
					}
					pictureContainer.append(pictureLink);
				} else {
					pictureContainer.append(picture);
				}
				paragraphWithPicture.replaceWith(pictureContainer);
			},
		);

		if (!showAll && index >= visibleCount) {
			cardElementWrapper.classList.add('article-cards__card-hidden');
		}

		articleCards.appendChild(cardElementWrapper);
	});

	addItemListSchema(listItems, titleElement.innerText?.trim());

	block.appendChild(articleCards);

	if (!showAll && cardElements.length > visibleCount) {
		const loadMoreContainer = document.createElement('div');
		loadMoreContainer.classList.add('article-cards__load-more-container');

		const loadMoreButton = document.createElement('button');
		loadMoreButton.classList.add(
			'article-cards__load-more',
			'black',
			'secondary',
			'small',
		);

		const placeholders = await fetchPlaceholders();
		loadMoreButton.textContent = placeholders.loadMore || 'Load More';
		loadMoreContainer.append(loadMoreButton);
		articleCards.parentElement?.appendChild(loadMoreContainer);

		loadMoreButton.addEventListener('click', () => {
			Array.from(articleCards.children).forEach((articleCard) => {
				articleCard.classList.remove('article-cards__card-hidden');
			});
			loadMoreContainer.style.display = 'none';
		});
	}
}
